/**
 * Input con label asociado y error inline.
 *
 * Cumple regla de marca:
 * - Label siempre asociado (for/id) — accesibilidad.
 * - Error en color bone-muted (no rojo dramático). La marca informa, no alarma.
 * - aria-invalid + aria-describedby para screen readers.
 */
import { forwardRef, useId } from "react";
import type { InputHTMLAttributes } from "react";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, error, hint, id, className = "", ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint && !error ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-xs uppercase tracking-widest text-gray-mid">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={errorId ?? hintId}
        className={
          // w-full + min-w-0: en Safari iOS los <input> sin ancho explícito
          // conservan su ancho intrínseco y empujan el contenedor más allá del
          // viewport (overflow horizontal). Ancho definido + permitir encoger.
          "w-full min-w-0 rounded-md border bg-carbon px-3 py-2.5 text-sm text-bone placeholder:text-gray-mid " +
          "focus:outline-none focus:ring-2 focus:ring-lime focus:ring-offset-2 focus:ring-offset-graphite " +
          (error ? "border-danger/40" : "border-border-subtle hover:border-border-strong") +
          " " +
          className
        }
        {...rest}
      />
      {error && (
        <p id={errorId} className="text-xs text-bone-muted">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-xs text-gray-mid">
          {hint}
        </p>
      )}
    </div>
  );
});
